import {
  addReferences,
  updateBasicData,
  updateCertificates,
  updateEducations,
  updateExperiences,
  updateProjectPreferences,
  updateSkills,
} from '@/services/Profile/Api';
import {
  BasicData,
  Certificates,
  Educations,
  Experiences,
  Profile,
  ProjectPreferences,
  References,
  Skills,
} from '@/models/Profile';
import { PreviewableSection } from '@/state/profile/profilePreview';

/**
 * Per-section save dispatch for accepted AI previews.
 *
 * The preview slot holds response-shaped Profile data; each save endpoint
 * wants its own request shape. This file owns that translation in one place
 * so callers can simply `saveSectionPreview(section, preview)`.
 *
 * Slice 2/3 caveat: stub-extracted data may lack fields the real save
 * endpoints require (skill tag IDs, reference externalIdObject, ISO country
 * codes). Those sections will 400 until Slice 3 wires the real extraction.
 */

const saveBasic = (data: BasicData) => updateBasicData({
  city: data.city,
  // Slice 3: extraction should yield ISO codes directly; stub passes the
  // country *name* here, which the backend will reject. Wired structurally.
  countryIsoCode: data.country,
  profileSummary: data.profileSummary,
});

const saveProjectPreferences = (data: ProjectPreferences) => updateProjectPreferences({
  countryIsoCode: data.country,
  postalCode: data.postalCode,
  geoRadius: data.radiusInKm,
  remoteWork: data.remoteWork,
  microProjects: data.microProjects,
  companySize: data.companySize,
  placeName: data.placeName,
  industryTagList: data.industrieList,
});

const saveSkills = (data: Skills) => updateSkills({
  userSkills: { userSkillList: data.skillList },
});

const saveReferences = (data: References) => addReferences({ references: data });

const saveEducations = (data: Educations) => updateEducations({ educations: data });

const saveCertificates = (data: Certificates) => updateCertificates({ certificates: data });

// experiencesText is HTML-sanitized inside updateExperiences and the call
// mutates its argument; clone so we don't write into the preview ref.
const saveExperiences = (data: Experiences) => updateExperiences({ ...data });

const NOOP = Promise.resolve();

// Takes the full preview overlay (Partial<Profile>) rather than the raw
// slot value so each case can read its own field with a typed property
// access — no `as` casts, no risk of section/data drift.
const saveSectionPreview = (
  section: PreviewableSection,
  preview: Partial<Profile>,
): Promise<unknown> => {
  switch (section) {
    case 'basic': return preview.basic ? saveBasic(preview.basic) : NOOP;
    case 'projectPreferences': return preview.projectPreferences ? saveProjectPreferences(preview.projectPreferences) : NOOP;
    case 'skills': return preview.skills ? saveSkills(preview.skills) : NOOP;
    case 'references': return preview.references ? saveReferences(preview.references) : NOOP;
    case 'educations': return preview.educations ? saveEducations(preview.educations) : NOOP;
    case 'certificates': return preview.certificates ? saveCertificates(preview.certificates) : NOOP;
    case 'projects': return preview.experiences ? saveExperiences(preview.experiences) : NOOP;
    default: return Promise.reject(new Error(`No save mapping for section: ${section}`));
  }
};

export default saveSectionPreview;
